import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Tag,
  Plus,
  Edit,
  Trash2,
  Check,
  Calendar,
  Package,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function PriceListModule({ businessId, subscription }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages"]);
  const { formatPrice, currencySymbol } = useCurrency();

  const [priceLists, setPriceLists] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [deleteListId, setDeleteListId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    effective_date: "",
    prices: [],
  });

  // Check subscription
  const canAccess = subscription?.is_active && subscription?.plan === "pro";

  const fetchData = async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [listsRes, packagesRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/price-lists`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/packages`, { credentials: "include" }),
      ]);
      
      if (listsRes.ok) {
        setPriceLists(await listsRes.json());
      }
      if (packagesRes.ok) {
        setPackages(await packagesRes.json());
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId && canAccess) {
      fetchData();
    }
  }, [businessId, canAccess]);

  // Sync packages with price list - include all packages
  const syncPackagesWithPrices = (existingPrices = []) => {
    return packages.map(pkg => {
      const existing = existingPrices.find(p => p.package_id === pkg.package_id);
      return {
        package_id: pkg.package_id,
        package_name: pkg.name,
        session_count: pkg.session_count,
        price: existing?.price || 0,
        discounted_price: existing?.discounted_price || 0,
      };
    });
  };

  const handleOpenModal = (list = null) => {
    if (list) {
      setEditingList(list);
      // Sync existing prices with current packages
      setFormData({
        name: list.name || "",
        effective_date: list.effective_date || "",
        prices: syncPackagesWithPrices(list.prices || []),
      });
    } else {
      setEditingList(null);
      // Initialize with all packages
      setFormData({
        name: "",
        effective_date: todayLocalString(),
        prices: syncPackagesWithPrices([]),
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingList(null);
    setFormData({ name: "", effective_date: "", prices: [] });
  };

  const handlePriceChange = (packageId, field, value) => {
    setFormData(prev => ({
      ...prev,
      prices: prev.prices.map(p => 
        p.package_id === packageId ? { ...p, [field]: parseInt(value) || 0 } : p
      ),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.effective_date) {
      toast.error(t("common:toast_listeAdiVeBaslangicTarihi"));
      return;
    }

    try {
      const url = editingList 
        ? `${API}/price-lists/${editingList.price_list_id}`
        : `${API}/price-lists`;
      
      const response = await fetch(url, {
        method: editingList ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: businessId,
          ...formData,
        }),
      });

      if (response.ok) {
        toast.success(editingList ? t("common:priceListUpdated") : t("common:priceListCreated"));
        handleCloseModal();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleActivate = async (priceListId) => {
    try {
      const response = await fetch(`${API}/price-lists/${priceListId}/activate`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success(t("settings:priceListActivated", "Fiyat listesi aktif edildi"));
        fetchData();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || t("common:operationFailed"));
      }
    } catch (e) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteListId) return;
    
    try {
      const response = await fetch(`${API}/price-lists/${deleteListId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t("settings:priceListDeleted"));
        fetchData();
      } else {
        toast.error(data.detail || "Silinemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeleteListId(null);
    }
  };

  if (!canAccess) {
    return (
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardContent className="p-8 text-center">
          <Tag className="w-12 h-12 text-grey mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-navy mb-2">{t('payments:priceList')}</h3>
          <p className="text-grey mb-4">
            {t("admin:premiumRequired")}
          </p>
          <Badge variant="outline" className="text-amber-600 border-amber-300">
            Pro
          </Badge>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">{t('payments:priceList')}</h2>
          <p className="text-grey">{t("common:donemselFiyatlariniziYonetin")}</p>
        </div>
        <Button
          className="bg-navy hover:bg-navy/90 rounded-xl"
          onClick={() => handleOpenModal()}
          disabled={packages.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("common:newList")}
        </Button>
      </div>

      {packages.length === 0 && (
        <Card className="border-0 shadow-md rounded-2xl bg-amber-50">
          <CardContent className="p-4 text-center text-amber-800">
            {t("packages:addPackagesFirst")}
          </CardContent>
        </Card>
      )}

      {/* Price Lists */}
      {priceLists.length > 0 ? (
        <div className="grid gap-4">
          {priceLists.map((list) => (
            <Card key={list.price_list_id} className={`border-0 shadow-md rounded-2xl ${list.is_active ? 'ring-2 ring-green-500' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${list.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <Tag className={`w-5 h-5 ${list.is_active ? 'text-green-600' : 'text-grey'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {list.name}
                        {list.is_active ? (
                          <Badge className="bg-green-100 text-green-700">{t('admin:active')}</Badge>
                        ) : (
                          <Badge variant="secondary">{t('common:passive')}</Badge>
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-sm text-grey">
                        <Calendar className="w-3 h-3" />
                        {t("common:startDateLabel")}: {list.effective_date && format(new Date(list.effective_date), "dd MMMM yyyy", { locale: getDateLocale() })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!list.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-300 hover:bg-green-50 rounded-xl"
                        onClick={() => handleActivate(list.price_list_id)}
                        data-testid={`activate-pricelist-${list.price_list_id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        {t("settings:makeActive", "Aktif Yap")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-navy"
                      onClick={() => handleOpenModal(list)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500"
                      onClick={() => setDeleteListId(list.price_list_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-xl overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="whitespace-nowrap">{t("packages:package")}</TableHead>
                        <TableHead className="whitespace-nowrap">{t("packages:session")}</TableHead>
                        <TableHead className="text-right whitespace-nowrap">{t('common:price')}</TableHead>
                        <TableHead className="text-right whitespace-nowrap">{t("common:discounted")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {list.prices?.map((item) => {
                        const pkg = packages.find(p => p.package_id === item.package_id);
                        return (
                          <TableRow key={item.package_id}>
                            <TableCell className="font-medium whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-grey flex-shrink-0" />
                                {item.package_name || pkg?.name || t("packages:deletedPackage")}
                              </div>
                            </TableCell>
                            <TableCell className="text-grey whitespace-nowrap">
                              {item.session_count || pkg?.session_count || "-"}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-navy whitespace-nowrap">
                              {formatPrice((item.price || 0))}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {item.discounted_price > 0 ? (
                                <span className="font-semibold text-green-600">
                                  {formatPrice(item.discounted_price)}
                                </span>
                              ) : (
                                <span className="text-grey">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(!list.prices || list.prices.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-grey py-4">
                            {t("common:noPriceAdded")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : packages.length > 0 ? (
        <Card className="border-0 shadow-md rounded-2xl">
          <CardContent className="p-8 text-center">
            <Tag className="w-12 h-12 text-grey mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-navy mb-2">{t("common:henuzFiyatListesiYok")}</h3>
            <p className="text-grey mb-4">
              {t("packages:createFirstPriceList")}
            </p>
            <Button
              className="bg-navy hover:bg-navy/90 rounded-xl"
              onClick={() => handleOpenModal()}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("common:createPriceList")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingList ? t('payments:editPriceList') : t('payments:newPriceList')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("common:listName")}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t("common:ph_ornOcak2025Fiyatlari")}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>{t("common:startDateRequired")}</Label>
                <DatePickerTR
                  value={formData.effective_date ? new Date(formData.effective_date) : null}
                  onChange={(date) => {
                    if (date) {
                      // Use local date to avoid UTC timezone shift
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      setFormData(prev => ({ ...prev, effective_date: `${year}-${month}-${day}` }));
                    } else {
                      setFormData(prev => ({ ...prev, effective_date: "" }));
                    }
                  }}
                  placeholder={t('common:selectDate')}
                />
                <p className="text-xs text-grey mt-1">{t("common:enGuncelTarihliListeAktif")}</p>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">{t("packages:paketFiyatlari")}</Label>
              <div className="border rounded-xl overflow-hidden overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="whitespace-nowrap">{t("packages:package")}</TableHead>
                      <TableHead className="whitespace-nowrap">{t("packages:session")}</TableHead>
                      <TableHead className="text-right whitespace-nowrap">{t("settings:priceLabel")}</TableHead>
                      <TableHead className="text-right whitespace-nowrap">{t("common:discounted")} ({currencySymbol})</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.prices.map((item) => (
                      <TableRow key={item.package_id}>
                        <TableCell className="font-medium whitespace-nowrap">
                          {item.package_name}
                        </TableCell>
                        <TableCell className="text-grey whitespace-nowrap">
                          {item.session_count || "-"}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Input
                            type="number"
                            value={item.price || ""}
                            onChange={(e) => handlePriceChange(item.package_id, "price", e.target.value)}
                            className="w-24 sm:w-28 ml-auto rounded-xl text-right"
                            placeholder="0"
                          />
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Input
                            type="number"
                            value={item.discounted_price || ""}
                            onChange={(e) => handlePriceChange(item.package_id, "discounted_price", e.target.value)}
                            className="w-24 sm:w-28 ml-auto rounded-xl text-right"
                            placeholder="0"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {formData.prices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-grey py-4">
                          {t("packages:noPackageDefined2")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-grey mt-2">
                {t("common:discountedPriceOptional")}
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={handleCloseModal} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button onClick={handleSubmit} className="bg-navy hover:bg-navy/90 rounded-xl">
              {editingList ? t('settings:update') : t('settings:create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteListId} onOpenChange={(open) => !open && setDeleteListId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("settings:deletePriceList")}</DialogTitle>
            <DialogDescription>
              {t("common:deletePriceListConfirm")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteListId(null)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button variant="destructive" onClick={confirmDelete} className="rounded-xl">
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
